import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	AlertManager,
	createLocalDataSource,
	useAlerts,
	useDeepComparison,
	useFilterHandler,
	useForceRerender,
	useSelection,
	useSortHandler,
	useUserTimezone
} from "client/Hooks";
import {
	DateString,
	DialogBackdropState,
	FormattedText,
	Icon,
	InlineOptionSelectorItem,
	OptionsMenu,
	Pagination,
	SearchInputForm,
	SelectItem,
	TableView,
	TableViewNoResultConfig,
	TagList,
	TagModel,
	ToolbarAction,
	ToolbarRegularAction,
	Typography,
	useConfirmationDialog
} from "client/Components";
import {
	AnnouncementListItemModel,
	AnnouncementListModel,
	AnnouncementStatus,
	archiveAnnouncements,
	deleteAnnouncements,
	listAnnouncements
} from "client/Api/Announcements/announcements";
import { contains, normalizeForSearch } from "client/Data/searching";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { TimeZone } from "client/DateAndTime/timezones";
import { FilterForm } from "client/Features/Settings/FilterForm/FilterForm";
import {
	generateTrackingId,
	isNil,
	isNotNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import { HubManagementDialogContentLayout } from "../../HubManagementDialogContentLayout";
import { useEditAnnouncementDialog } from "../../../AnnouncementManagement/EditAnnouncementDialog/EditAnnouncementDialog";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { useCreateHubTemplateDialog } from "../../../Templates/CreateHubTemplateDialog";
import { useCreateHubItemDialog } from "client/Features/Hub/CreateHubItemDialog/CreateHubItemDialog";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import styles from "./AnnouncementsOverview.module.less";
import { fromArray } from "client_legacy/ndaw/mapHelpers";

export type AnnouncementsOverviewProps = {
	onParentBackdropChange: (backdrop: DialogBackdropState) => void;
	isMobile: boolean;
	isResponsive: boolean;
	onItemsChanged: () => void;
};

export const AnnouncementsOverview = (props: AnnouncementsOverviewProps): ReactElement => {
	const {
		onParentBackdropChange,
		onItemsChanged,
		isMobile,
		isResponsive
	} = props;

	const [announcementList, setAnnouncementList] = useState<AnnouncementListModel>({
		announcements: [],
		categories: [],
		canCreateTemplates: false
	});

	const [selectedAnnouncements, setSelectedAnnouncements] = useState<readonly AnnouncementListItemModel[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [filter, setFilter] = useState<FilterState>({
		title: "",
		activeCategoryId: null,
		createdBy: "",
		modifiedBy: "",
		status: null
	});
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [sortingState, setSortingState] = useState<DataSourceSortableState<AnnouncementListItemModel>>({
		direction: DataSourceSortDirection.Descending,
		propertyName: "startTime"
	});

	const alertManager: AlertManager = useAlerts();
	const [editAnnouncementDialog, openAnnouncementDialog] = useEditAnnouncementDialog();
	const [createHubItemDialog, openCreateHubItemDialog] = useCreateHubItemDialog();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [reload, dataVersion] = useForceRerender();
	const [createHubTemplateDialog, openCreateHubTemplateDialog] = useCreateHubTemplateDialog();

	const editAnnouncementHandler = useCallback(
		async (announcement?: AnnouncementListItemModel): Promise<void> => {
			onParentBackdropChange("invisible");
			let result: TryResult<void>;

			if (isNil(announcement?.id)) {
				result = await openCreateHubItemDialog({
					kind: HubItemKind.Announcement
				});
			} else {
				result = await openAnnouncementDialog({
					mode: {
						kind: "edit",
						announcementId: announcement.id
					}
				});
			}

			if (result.wasSuccessful) {
				reload();
				onItemsChanged();
			}

			onParentBackdropChange("visible");
		},
		[
			onParentBackdropChange,
			openAnnouncementDialog,
			reload,
			openCreateHubItemDialog,
			onItemsChanged
		]
	);

	const deleteAnnouncementsHandler = useCallback(
		async (announcements: readonly AnnouncementListItemModel[]): Promise<void> => {
			try {
				const result: TryResult<void> = await openConfirmationDialog({
					title: localize("_AreYouSureYouWantToDeleteThisItem(s)"),
					children: hasAnnouncementWithMultipleCategories(announcements)
						? localize("_DeleteHubItemsWithMultipleCategoriesInfoText")
						: undefined,
					confirmText: localize("_Delete"),
					trackingId:"announcement-delete-confirmation-dialog",
				});

				if (!result.wasSuccessful) {
					return;
				}

				setIsBusy(true);
				setSelectedAnnouncements([]);
				await deleteAnnouncements(announcements.map(a => a.id));
				setIsBusy(false);
				reload();
				onItemsChanged();
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}
				alertManager.showError(localize("_ErrorDeletingAnnouncements"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			openConfirmationDialog,
			reload,
			onItemsChanged
		]
	);

	const archiveAnnouncementsHandler = useCallback(
		async (announcements: readonly AnnouncementListItemModel[]): Promise<void> => {
			try {
				const result: TryResult<void> = await openConfirmationDialog({
					title: localize("_AreYouSureYouWantToArchiveThisItem(s)"),
					confirmText: localize("_Archive"),
					children: hasAnnouncementWithMultipleCategories(announcements)
						? localize("_ArchiveHubItemsWithMultipleCategoriesInfoText")
						: undefined,
					trackingId:"announcement-archive-confirmation-dialog",
				});

				if (!result.wasSuccessful) {
					return;
				}

				setIsBusy(true);
				await archiveAnnouncements(announcements.map(a => a.id));
				setIsBusy(false);
				reload();
				onItemsChanged();
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}
				alertManager.showError(localize("_ErrorDeletingAnnouncements"));
				setIsBusy(false);
			}

		},
		[
			alertManager,
			openConfirmationDialog,
			reload,
			onItemsChanged
		]
	);

	const saveAsTemplateHandler = useCallback(
		async (item: AnnouncementListItemModel): Promise<void> => {
			onParentBackdropChange("invisible");

			await openCreateHubTemplateDialog({
				kind: HubItemKind.Announcement,
				basedOn: {
					hubItemId: item.id
				}
			});

			onParentBackdropChange("visible");
		},
		[
			onParentBackdropChange,
			openCreateHubTemplateDialog
		]
	);

	const duplicateHandler = useCallback(
		async (item: AnnouncementListItemModel): Promise<void> => {
			onParentBackdropChange("invisible");

			const { wasSuccessful } = await openAnnouncementDialog({
				mode: {
					kind: "duplicate",
					announcementToCopyId: item.id
				}
			});

			if (wasSuccessful) {
				reload();
				onItemsChanged();
			}

			onParentBackdropChange("visible");
		},
		[
			onItemsChanged,
			onParentBackdropChange,
			openAnnouncementDialog,
			reload
		]
	);

	useEffect(
		() => {
			hasImplicitDependencyOn(dataVersion);

			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					const announcementList: AnnouncementListModel = await listAnnouncements(signal);

					setAnnouncementList(announcementList);

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, dataVersion]
	);

	const {
		sortingFor
	} = useSortHandler(sortingState, setSortingState);

	const {
		filteringFor
	} = useFilterHandler<FilterState>(filter, setFilter);

	const itemsModifiersVersion: number = useDeepComparison([
		sortingState,
		searchTerm,
		filter
	]);
	const userTimezone: TimeZone = useUserTimezone();

	useEffect(
		() => setCurrentPageNumber(0),
		[itemsModifiersVersion]
	);

	const categoryIdOptions = useMemo(
		(): readonly InlineOptionSelectorItem<number | null>[] => {
			return [
				{
					text: localize("_AllAnnouncements"),
					value: null,
					valueTrackingId: "all-announcements"
				},
				...announcementList.categories.map(c => ({
					text: c.name,
					value: c.id
				}))
			];
		},
		[announcementList.categories]
	);

	const categoryTagMap = useMemo(
		(): ReadonlyMap<number, TagModel<number>> => {
			return fromArray(
				announcementList.categories,
				(x): number => x.id,
				(x): TagModel<number> => ({
					text: x.name,
					value: x.id,
					color: "secondary"
				})
			);
		},
		[announcementList.categories]
	);

	const getCategoriesTags = useCallback(
		(categoryIds: readonly number[]): readonly TagModel<number>[] => {
			const tags: TagModel<number>[] = [];

			for (const id of categoryIds) {
				const tag: TagModel<number> | undefined = categoryTagMap.get(id);

				if (isNotNil(tag)) {
					tags.push(tag);
				}
			}

			return tags;
		},
		[categoryTagMap]
	);

	const totalItemCount: number = announcementList.announcements
		.length;

	const {
		currentItems,
		pagination
	} = useAnnouncementDataSource(
		announcementList.announcements,
		{
			searchTerm,
			filtering: filter,
			pagination: {
				currentPageNumber
			},
			sorting: sortingState
		}
	);

	const {
		selectionFor,
		multiSelection
	} = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems: selectedAnnouncements,
			setSelectedItems: setSelectedAnnouncements
		}
	);

	const actionContext = useMemo(
		(): AnnouncementActionContext => {
			return {
				selectedAnnouncements,
				canCreateTemplates: announcementList.canCreateTemplates,
				isMobile
			};
		},
		[
			announcementList.canCreateTemplates,
			isMobile,
			selectedAnnouncements
		]
	);

	const selectionActions = useMemo(
		(): readonly AnnouncementAction[] => {
			return getSelectionActions(
				() => setSelectedAnnouncements([])
			);
		},
		[]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<AnnouncementActionContext> => {
			return getMainAction(
				editAnnouncementHandler
			);
		},
		[editAnnouncementHandler]
	);

	const contextualActions = useMemo(
		(): readonly AnnouncementAction[] => {
			return getContextualActions(
				editAnnouncementHandler,
				deleteAnnouncementsHandler,
				archiveAnnouncementsHandler,
				saveAsTemplateHandler,
				duplicateHandler
			);
		},
		[
			archiveAnnouncementsHandler,
			deleteAnnouncementsHandler,
			duplicateHandler,
			editAnnouncementHandler,
			saveAsTemplateHandler
		]
	);

	const hasSelection: boolean = selectedAnnouncements.length > 0;

	const actions = useMemo(
		(): readonly AnnouncementAction[] => {
			return buildActionsForTopBar<AnnouncementActionContext, AnnouncementAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			mainAction,
			selectionActions,
			contextualActions,
			hasSelection,
			isResponsive
		]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			const message: string = localize(
				totalItemCount === 0
					? "_ThereAreNoAnnouncementsYet"
					: isNotNilOrEmpty(searchTerm)
						? "_NoAnnouncementsMatchedYourSearchKeywords"
						: "_NoAnnouncementsInThisCategory"
			);

			if (isNotNilOrEmpty(searchTerm)) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message
				};
			}

			return {
				icon: "bullhorn",
				title: localize("_NoAnnouncementsYet"),
				message
			};
		},
		[
			searchTerm,
			totalItemCount
		]
	);

	const multipleCategoryTitleTooltip: string = localize("_ItemIsInMultipleCategoriesPermissionInfoText");

	return (
		<HubManagementDialogContentLayout
			title={localize("_Announcements")}
			isMobile={isMobile}
			isResponsive={isResponsive}
			actions={actions}
			actionContext={actionContext}
			hasSelection={hasSelection}
			trackingId="announcements-overview"
			isBusy={isBusy}
		>
			<HubManagementDialogContentLayout.Filter>
				<SearchInputForm
					trackingId="announcement-search-form"
					onChange={setSearchTerm}
					placeholder={
						<FormattedText
							text={localize("_FilterByProperties")}
							thingsToReplace={[
								{
									what: "{{Properties}}",
									filler: (
										<Typography
											weight="bold"
											isItalic
										>
											{localize("_Title")}
										</Typography>
									)
								}
							]}
						/>
					}
					height="large"
					icon="filter"
				/>
				<OptionsMenu
					value={filteringFor("activeCategoryId").value}
					onChange={filteringFor("activeCategoryId").setValue}
					options={categoryIdOptions}
					trackingId="announcement-category-selector"
					icon="filter"
					title={localize("_Category")}
					buttonSize="large"
				/>
			</HubManagementDialogContentLayout.Filter>
			<HubManagementDialogContentLayout.Table>
				<TableView
					items={currentItems}
					itemKey="id"
					multiSelectable={multiSelection}
					height="stretch"
					columns={[
						{
							title: localize("_Title"),
							width: 270,
							sortable: sortingFor("title"),
							filterable: filteringFor("title"),
							trackingId: "title"
						},
						{
							title: localize("_PublicationStart"),
							width: 220,
							sortable: sortingFor("startTime"),
							trackingId: "start-time"
						},
						{
							title: localize("_PublicationEnd"),
							width: 220,
							sortable: sortingFor("endTime"),
							trackingId: "end-time"
						},
						{
							title: localize("_DateModified"),
							width: 220,
							sortable: sortingFor("modifiedDate"),
							trackingId: "modified-date"
						},
						{
							title: localize("_Status"),
							width: 150,
							sortable: sortingFor("status"),
							filterable: {
								columnFilterableFormFactory: (onClose) => (
									<FilterForm
										filterable={filteringFor("status")}
										onClose={onClose}
										statusFilterOptions={statusFilterOptions}
									/>
								),
								isActive: isNotNil(filter.status)
							},
							trackingId: "status"
						},
						{
							title: localize("_CreatedBy"),
							width: 160,
							sortable: sortingFor("createdByUserName"),
							filterable: filteringFor("createdBy"),
							trackingId: "created-by"
						},
						{
							title: localize("_ModifiedBy"),
							width: 160,
							sortable: sortingFor("modifiedByUserName"),
							filterable: filteringFor("modifiedBy"),
							trackingId: "modified-by"
						},
						{
							title: localize("_Comments"),
							width: 90,
							sortable: sortingFor("commentsCount"),
							trackingId: "comments-count"
						},
						{
							title: localize("_Reactions"),
							width: 90,
							sortable: sortingFor("reactionsCount"),
							trackingId: "likes-count"
						},
						{
							title: localize("_Views"),
							width: 90,
							sortable: sortingFor("viewsCount"),
							trackingId: "views-count"
						},
						{
							title: localize("_Category(ies)"),
							width: 200,
							isExpensiveToRender: true,
							trackingId: "category-name"
						},
						{
							title: localize("_Pinned"),
							width: 90,
							sortable: sortingFor("isPinned"),
							trackingId: "pinned"
						},
						{
							title: localize("_Tags"),
							width: 200,
							trackingId: "tags",
							isExpensiveToRender: true
						}
					]}
					trackingId="announcement-grid"
					noResultConfig={noResultConfig}
				>
					{a => (
						<TableView.Row
							key={a.id}
							selectable={selectionFor(a)}
							onExecute={() => editAnnouncementHandler(a)}
							trackingId={a.id.toString()}
						>
							<TableView.Cell>
								<div className={styles.titleAndMoreInfoContainer}>
									<span className={styles.title}>
										{a.title}
									</span>
									{a.categoryIds.length > 1 && (
										<Icon
											tooltip={multipleCategoryTitleTooltip}
											value={{
												kind: "font",
												name: "circle-info",
												color: "info"
											}}
										/>
									)}
								</div>
							</TableView.Cell>
							<TableView.Cell>
								{isNotNil(a.startTime) && (
									<DateString
										value={a.startTime}
										format="RelativeDayWithTime"
										timezone={userTimezone}
									/>
								)}
							</TableView.Cell>
							<TableView.Cell>
								{isNotNil(a.endTime) && (
									<DateString
										value={a.endTime}
										format="RelativeDayWithTime"
										timezone={userTimezone}
									/>
								)}
							</TableView.Cell>
							<TableView.Cell>
								<DateString
									value={a.modifiedDate}
									format="RelativeDayWithTime"
									timezone={userTimezone}
								/>
							</TableView.Cell>
							<TableView.Cell>
								{localize(announcementStatusToLocalizationKeyMap[a.status] )}
							</TableView.Cell>
							<TableView.Cell>
								{a.createdByUserName}
							</TableView.Cell>
							<TableView.Cell>
								{a.modifiedByUserName}
							</TableView.Cell>
							<TableView.Cell>
								{a.commentsCount}
							</TableView.Cell>
							<TableView.Cell>
								{a.reactionsCount}
							</TableView.Cell>
							<TableView.Cell>
								{a.viewsCount}
							</TableView.Cell>
							<TableView.Cell>
								<TagList
									trackingId={generateTrackingId("categories-tag-list", `|${a.id}`)}
									showMoreTagsColor="secondary"
									tags={getCategoriesTags(a.categoryIds)}
									dialogTitle={localize("_Categories")}
								/>
							</TableView.Cell>
							<TableView.Cell>
								{a.isPinned &&
									<Icon value="check" size="large" />
								}
							</TableView.Cell>
							<TableView.Cell>
								<TagList
									trackingId="announcement-tag-list"
									showMoreTagsColor="secondary"
									tags={
										a.tags
											.map(t => ({
												value: t.id,
												text: t.name,
												color: "secondary"
											}))
									}
								/>
							</TableView.Cell>
						</TableView.Row>
					)}
				</TableView>
				<Pagination
					{...pagination}
					onPageNumberChanged={setCurrentPageNumber}
					trackingId="announcements-pagination"
				/>
			</HubManagementDialogContentLayout.Table>
			{isResponsive && mainAction.canExecute(actionContext) && (
				<HubManagementDialogContentLayout.MainButton
					onClick={() => mainAction.execute(actionContext)}
					trackingId={mainAction.name}
				/>
			)}
			{createHubTemplateDialog}
			{editAnnouncementDialog}
			{createHubItemDialog}
			{confirmationDialog}
		</HubManagementDialogContentLayout>
	);
};
AnnouncementsOverview.displayName="AnnouncementsOverview";

const statusFilterOptions: readonly SelectItem<AnnouncementStatus>[] = [
	{
		value: AnnouncementStatus.Draft,
		text: localize("_Draft")
	},
	{
		value: AnnouncementStatus.Planned,
		text: localize("_Planned")
	},
	{
		value: AnnouncementStatus.Published,
		text: localize("_Published")
	},
	{
		value: AnnouncementStatus.Archived,
		text: localize("_Archived")
	}
];

export const announcementStatusToLocalizationKeyMap: { [key in AnnouncementStatus]: LocalizationKey; } = {
	[AnnouncementStatus.Planned]: "_Planned",
	[AnnouncementStatus.Published]: "_Published",
	[AnnouncementStatus.Archived]: "_Archived",
	[AnnouncementStatus.Draft]: "_Draft"
};

type FilterState = {
	title: string;
	activeCategoryId: number | null;
	status: AnnouncementStatus | null;
	createdBy: string;
	modifiedBy: string;
};

function applyFilter(
	items: readonly AnnouncementListItemModel[],
	filter: FilterState
): readonly AnnouncementListItemModel[] {
	const normalizedTitle: string = normalizeForSearch(filter.title);
	const normalizedCreatedBy: string = normalizeForSearch(filter.createdBy);
	const normalizedModifiedBy: string = normalizeForSearch(filter.modifiedBy);

	return items.filter(i =>
		(filter.activeCategoryId === null || i.categoryIds.includes(filter.activeCategoryId))
			&& (filter.status === null || i.status === filter.status)
			&& (normalizedCreatedBy === "" || contains(i.createdByUserName, normalizedCreatedBy))
			&& (normalizedModifiedBy === "" || contains(i.modifiedByUserName, normalizedModifiedBy))
			&& (normalizedTitle === "" || contains(i.title, normalizedTitle))
	);
}

const useAnnouncementDataSource = createLocalDataSource<AnnouncementListItemModel, FilterState>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) => contains(item.title, searchTerm)
		},
		filtering: { applyFilter },
		hasPagination: true
	})
	.getHook();

type AnnouncementActionContext = {
	selectedAnnouncements: readonly AnnouncementListItemModel[];
	canCreateTemplates: boolean;
	isMobile: boolean;
};

type AnnouncementAction = ToolbarAction<AnnouncementActionContext>;

function getContextualActions(
	editAnnouncementHandler: (item: AnnouncementListItemModel) => void,
	deleteAnnouncementsHandler: (items: readonly AnnouncementListItemModel[]) => void,
	archiveAnnouncementsHandler: (item: readonly AnnouncementListItemModel[]) => void,
	saveAsTemplateHandler: (item: AnnouncementListItemModel) => void,
	duplicateAnnouncementHandler: (item: AnnouncementListItemModel) => void
): readonly AnnouncementAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "editAnnouncement",
			title: localize("_Edit"),
			canExecute: (c) => c.selectedAnnouncements.length === 1
				&& c.selectedAnnouncements.every(a => a.canBeEditedByUser),
			execute: (c) => editAnnouncementHandler(c.selectedAnnouncements[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "deleteAnnouncements",
			title: localize("_Delete"),
			canExecute: (c) => c.selectedAnnouncements.length > 0
				&& c.selectedAnnouncements.every(a => a.canBeEditedByUser),
			execute: (c) => deleteAnnouncementsHandler(c.selectedAnnouncements),
			icon: "trash-can"
		},
		{
			kind: ActionKind.Regular,
			name: "archiveAnnouncements",
			title: localize("_Archive"),
			canExecute: (c) => c.selectedAnnouncements
				.some(x => x.status !== AnnouncementStatus.Archived)
					&& c.selectedAnnouncements.every(a => a.canBeEditedByUser),
			execute: (c) => archiveAnnouncementsHandler(c.selectedAnnouncements),
			icon: "box-archive"
		},
		{
			kind: ActionKind.Regular,
			name: "duplicateAnnouncement",
			title: localize("_Duplicate"),
			canExecute: (c) => c.selectedAnnouncements.length === 1
				&& c.selectedAnnouncements.every(a => a.canBeEditedByUser),
			execute: (c) => duplicateAnnouncementHandler(c.selectedAnnouncements[0]),
			icon: "copy"
		},
		{
			kind: ActionKind.Regular,
			name: "saveAsTemplate",
			title: localize("_SaveAsTemplate"),
			canExecute: (c) => c.canCreateTemplates
				&& c.selectedAnnouncements.length === 1,
			execute: (c) => saveAsTemplateHandler(c.selectedAnnouncements[0]),
			icon: "floppy-disk"
		}
	];
}

function getSelectionActions(
	deselectHandler: () => void
): readonly AnnouncementAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selectedAnnouncements),
			icon: "circle-xmark",
			canExecute: (c) => c.selectedAnnouncements.length > 0,
			execute: () => deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selectedAnnouncements.length > 0
		}
	];
}

function getMainAction(
	newAnnouncementHandler: () => void
): ToolbarRegularAction<AnnouncementActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "createNewAnnouncement",
		title: localize("_New"),
		canExecute: () => true,
		execute: () => newAnnouncementHandler(),
		icon: "plus",
		isPrimary: true
	};
}

function hasAnnouncementWithMultipleCategories(
	announcements: readonly AnnouncementListItemModel[]
): boolean {
	return announcements.some(
		x => x.categoryIds.length > 1
	);
}